import type { User } from '@hendrx/authentication/contracts/client';
import type { Capability, UiElementsType } from '@hendrx/authorization/constants';
import { redirect } from '@tanstack/react-router';
import type { ParsedLocation } from '@tanstack/react-router';

export type Role = User['role'];
export type AuthorizedUiElement = UiElementsType;

export type GuardAuth = {
  user: User | null;
  isUser: boolean;
  isAdmin: boolean | null;
  roles: Role[];
};

export type GuardPermissions = {
  authorizedUiElements: AuthorizedUiElement[];
  /**
   * What the caller may do, as answered by the principal. Every capability a route guard asks about is
   * ambient ("may this caller reach this surface at all?"), so a flat list is the whole contract — there is
   * no resource here to match conditions against, and the server stays authoritative for per-item access.
   */
  capabilities: Capability[];
};

export type GuardContext = {
  auth: GuardAuth;
  permissions: GuardPermissions;
  location: ParsedLocation;
};

/**
 * Permissive redirect options — TanStack Router's full NavigateOptions type is
 * tied to registered router search schemas, making it impractical to use inside
 * generic guard primitives. Guards construct redirect targets as plain objects;
 * the actual type-safe throw happens inside createRouteGuard via a cast.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type GuardRedirectOptions = { to: string; search?: Record<string, any>; params?: Record<string, string> };

export type GuardResult = { allow: true } | { allow: false; redirect: GuardRedirectOptions };

export type GuardRule = (ctx: GuardContext) => GuardResult | Promise<GuardResult>;

type BeforeLoadArgs = {
  context: { auth: GuardAuth; permissions: GuardPermissions };
  location: ParsedLocation;
};

function buildGuardCtx(args: BeforeLoadArgs): GuardContext {
  return {
    auth: args.context.auth,
    permissions: args.context.permissions,
    location: args.location
  };
}

export const createRouteGuard =
  (...rules: GuardRule[]) =>
  async (args: BeforeLoadArgs): Promise<void> => {
    for (const rule of rules) {
      const result = await rule(buildGuardCtx(args));
      if (!result.allow) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        throw redirect(result.redirect as any);
      }
    }
  };
